import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  AddButton,
  ThinDropdown,
} from "../../../components/common/GeneralComponent";
import { fetchData, submitFormWithErrors } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getGrantTypeConfig } from "../grantTypeConfig";

const MAX_CURRENCY_AMOUNT = 9999999999999.99;
const CURRENCY_MAX_MESSAGE = "Amount is too large (max 13 digits)";

const GrantDetails = ({
  grantId,
  grantData,
  onNext,
  isEditMode,
  showNextOnly,
  onStoreFormData,
  onValidationChange,
  initialData,
  onGrantUpdated,
  grantType = "sme",
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cfg = getGrantTypeConfig(grantType);
  const isSedf = grantType === "sedf-enterprise";
  const [loading, setLoading] = useState(false);
  const [qualificationFile, setQualificationFile] = useState(null);

  const validationSchema = Yup.object({
    project_cost_pkr: Yup.number()
      .min(1, "Project cost PKR must be at least 1")
      .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
      .required("Project cost PKR is required"),
    project_cost_euro: Yup.number()
      .min(1, "Project cost Euro must be at least 1")
      .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
      .required("Project cost Euro is required"),
    applicant_share_pkr: Yup.number()
      .min(1, "Applicant share PKR must be at least 1")
      .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
      .required("Applicant share PKR is required"),
    applicant_share_euro: Yup.number()
      .min(1, "Applicant share Euro must be at least 1")
      .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
      .required("Applicant share Euro is required"),
    unido_grant_share_pkr: Yup.number()
      .min(1, "UNIDO grant share PKR must be at least 1")
      .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
      .required("UNIDO grant share PKR is required"),
    unido_grant_share_euro: Yup.number()
      .min(1, "UNIDO grant share Euro must be at least 1")
      .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
      .required("UNIDO grant share Euro is required"),
    sedf_share_pkr: isSedf
      ? Yup.number()
          .min(1, "SEDF share PKR must be at least 1")
          .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
          .required("SEDF share PKR is required")
      : Yup.number().nullable(),
    sedf_share_euro: isSedf
      ? Yup.number()
          .min(1, "SEDF share Euro must be at least 1")
          .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
          .required("SEDF share Euro is required")
      : Yup.number().nullable(),

    // Business type - required
    business_entity: Yup.string().required("Business entity is required"),
    business_entity_other: Yup.string().when("business_entity", {
      is: "Other/Specify",
      then: (schema) => schema.required("Please specify other business entity").matches(/^[^0-9]*$/, "Numbers are not allowed"),
      otherwise: (schema) => schema.nullable(),
    }),

    // Evaluation fields - all required
    evaluation_date: Yup.date()
      .required("Date of evaluation/qualification review is required")
      .typeError("Please enter a valid date")
      .test(
        "not-after-effective-date",
        "Date of evaluation cannot be later than the effective date",
        function (value) {
          const effectiveDate = this.parent.effective_date;

          if (!value || !effectiveDate) return true;
          return new Date(value) <= new Date(effectiveDate);
        }
      ),
    combined_score_percentage: Yup.number()
      .min(0, "Score must be between 0 and 100")
      .max(100, "Score must be between 0 and 100")
      .required("Combined score percentage is required"),

    // Grant agreement details - now required
    grant_agreement_number: Yup.string()
      .required("Grant agreement number is required")
      .min(3, "Grant agreement number must be at least 3 characters"),
    effective_date: Yup.date()
      .required("Effective date (signed) is required")
      .typeError("Please enter a valid effective date"),
    expiry_date: Yup.date()
      .required("Expiry date is required")
      .typeError("Please enter a valid expiry date")
      .min(
        Yup.ref("effective_date"),
        "Expiry date must be after effective date"
      ),

    // Approved grant amounts (entered in Million) - now required
    approved_grant_amount_pkr: Yup.number()
      .positive("Approved grant amount PKR must be positive")
      .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
      .required("Approved grant amount PKR is required")
      .test(
        "max-project-cost-pkr-million",
        "Approved grant amount PKR cannot exceed Project Cost PKR (Million)",
        function (value) {
          const projectCostPkr = parseFloat(this.parent.project_cost_pkr);
          if (value === undefined || value === null || isNaN(projectCostPkr)) {
            return true;
          }
          return value <= projectCostPkr / 1000000;
        }
      ),
    approved_grant_amount_euro: Yup.number()
      .positive("Approved grant amount Euro must be positive")
      .max(MAX_CURRENCY_AMOUNT, CURRENCY_MAX_MESSAGE)
      .required("Approved grant amount Euro is required")
      .test(
        "max-project-cost-euro-million",
        "Approved grant amount Euro cannot exceed Project Cost Euro (Million)",
        function (value) {
          const projectCostEuro = parseFloat(this.parent.project_cost_euro);
          if (value === undefined || value === null || isNaN(projectCostEuro)) {
            return true;
          }
          return value <= projectCostEuro / 1000000;
        }
      ),

    // Qualification minutes uploaded - required boolean
    qualification_minutes_uploaded: Yup.boolean()
      .required("Please specify if qualification minutes are uploaded")
      .oneOf([true, false], "Please select Yes or No"),
  });

  const formik = useFormik({
    initialValues: {
      project_cost_pkr:
        initialData?.project_cost_pkr || grantData?.project_cost_pkr || "",
      project_cost_euro:
        initialData?.project_cost_euro || grantData?.project_cost_euro || "",
      applicant_share_pkr:
        initialData?.applicant_share_pkr ||
        grantData?.applicant_share_pkr ||
        "",
      applicant_share_euro:
        initialData?.applicant_share_euro ||
        grantData?.applicant_share_euro ||
        "",
      unido_grant_share_pkr:
        initialData?.unido_grant_share_pkr ||
        grantData?.unido_grant_share_pkr ||
        "",
      unido_grant_share_euro:
        initialData?.unido_grant_share_euro ||
        grantData?.unido_grant_share_euro ||
        "",
      sedf_share_pkr:
        initialData?.sedf_share_pkr || grantData?.sedf_share_pkr || "",
      sedf_share_euro:
        initialData?.sedf_share_euro || grantData?.sedf_share_euro || "",
      business_entity:
        initialData?.business_entity || grantData?.business_entity || "",
      business_entity_other:
        initialData?.business_entity_other ||
        grantData?.business_entity_other ||
        "",
      evaluation_date:
        initialData?.evaluation_date || grantData?.evaluation_date || "",
      combined_score_percentage:
        initialData?.combined_score_percentage ||
        grantData?.combined_score_percentage ||
        "",
      grant_agreement_number:
        initialData?.grant_agreement_number ||
        grantData?.grant_agreement_number ||
        "",
      effective_date:
        initialData?.effective_date || grantData?.effective_date || "",
      expiry_date: initialData?.expiry_date || grantData?.expiry_date || "",
      approved_grant_amount_pkr:
        initialData?.approved_grant_amount_pkr ||
        grantData?.approved_grant_amount_pkr ||
        "",
      approved_grant_amount_euro:
        initialData?.approved_grant_amount_euro ||
        grantData?.approved_grant_amount_euro ||
        "",
      qualification_minutes_uploaded:
        initialData?.qualification_minutes_uploaded ??
        grantData?.qualification_minutes_uploaded ??
        "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Validate project cost calculation
        const projectCostPKR = parseFloat(values.project_cost_pkr) || 0;
        const totalSharePKR =
          (parseFloat(values.applicant_share_pkr) || 0) +
          (parseFloat(values.unido_grant_share_pkr) || 0) +
          (parseFloat(values.sedf_share_pkr) || 0);
        const projectCostEuro = parseFloat(values.project_cost_euro) || 0;
        const totalShareEuro =
          (parseFloat(values.applicant_share_euro) || 0) +
          (parseFloat(values.unido_grant_share_euro) || 0) +
          (parseFloat(values.sedf_share_euro) || 0);

        if (Math.abs(projectCostPKR - totalSharePKR) > 0.01) {
          toast.error(
            isSedf
              ? "Project Cost PKR must equal Applicant Share PKR + UNIDO Grant Share PKR + SEDF Share PKR"
              : "Project Cost PKR must equal Applicant Share PKR + UNIDO Grant Share PKR"
          );
          setLoading(false);
          return;
        }

        if (Math.abs(projectCostEuro - totalShareEuro) > 0.01) {
          toast.error(
            isSedf
              ? "Project Cost Euro must equal Applicant Share Euro + UNIDO Grant Share Euro + SEDF Share Euro"
              : "Project Cost Euro must equal Applicant Share Euro + UNIDO Grant Share Euro"
          );
          setLoading(false);
          return;
        }

        // Prepare form data
        const formData = new FormData();

        // Add all form values
        Object.keys(values).forEach((key) => {
          if (
            values[key] !== null &&
            values[key] !== undefined &&
            values[key] !== ""
          ) {
            formData.append(key, values[key]);
          }
        });

        // Add qualification file if selected
        if (qualificationFile) {
          formData.append("qualification_minutes_file", qualificationFile);
          formData.append("qualification_minutes_uploaded", true);
        }

        // Make API call to update grant
        const response = await fetchData(
          `${cfg.apiBase}/${grantId}`,
          "PUT",
          formData,
          {},
          {},
          navigate,
          "",
          dispatch
        );

        if (response) {
          toast.success("Grant details updated successfully");

          // Call the callback to refresh data if provided
          if (onGrantUpdated) {
            onGrantUpdated();
          }

          // Store form data if callback provided (for multi-step forms)
          if (onStoreFormData) {
            const dataToStore = { ...values };
            if (qualificationFile) {
              dataToStore.qualification_minutes_file = qualificationFile;
            }
            onStoreFormData(dataToStore);
          }

          // Call onNext if in multi-step form and not edit mode
          if (onNext && !isEditMode) {
            onNext();
          }
        }
      } catch (error) {
        console.error("Error updating grant details:", error);
        toast.error("Failed to update grant details");
      } finally {
        setLoading(false);
      }
    },
  });

  // Memoize validation calculation to prevent unnecessary recalculations
  const validationResult = useMemo(() => {
    const projectCostPKR = parseFloat(formik.values.project_cost_pkr) || 0;
    const totalSharePKR =
      (parseFloat(formik.values.applicant_share_pkr) || 0) +
      (parseFloat(formik.values.unido_grant_share_pkr) || 0) +
      (parseFloat(formik.values.sedf_share_pkr) || 0);
    const projectCostEuro = parseFloat(formik.values.project_cost_euro) || 0;
    const totalShareEuro =
      (parseFloat(formik.values.applicant_share_euro) || 0) +
      (parseFloat(formik.values.unido_grant_share_euro) || 0) +
      (parseFloat(formik.values.sedf_share_euro) || 0);

    const costMatchPKR = Math.abs(projectCostPKR - totalSharePKR) < 0.01;
    const costMatchEuro = Math.abs(projectCostEuro - totalShareEuro) < 0.01;

    return formik.isValid && costMatchPKR && costMatchEuro;
  }, [
    formik.isValid,
    formik.values.project_cost_pkr,
    formik.values.applicant_share_pkr,
    formik.values.unido_grant_share_pkr,
    formik.values.sedf_share_pkr,
    formik.values.project_cost_euro,
    formik.values.applicant_share_euro,
    formik.values.unido_grant_share_euro,
    formik.values.sedf_share_euro,
  ]);

  // Memoize form data to prevent unnecessary recreations
  const currentFormData = useMemo(() => {
    const dataToStore = { ...formik.values };
    if (qualificationFile) {
      dataToStore.qualification_minutes_file = qualificationFile;
    }
    return dataToStore;
  }, [formik.values, qualificationFile]);

  const handleValidationChange = useCallback(() => {
    if (onValidationChange) {
      onValidationChange(validationResult);
    }
  }, [onValidationChange, validationResult]);

  const handleStoreFormData = useCallback(() => {
    if (onStoreFormData && !isEditMode) {
      onStoreFormData(currentFormData);
    }
  }, [onStoreFormData, currentFormData, isEditMode]);

  useEffect(() => {
    handleValidationChange();
  }, [handleValidationChange]);

  useEffect(() => {
    handleStoreFormData();
  }, [handleStoreFormData]);

  useEffect(() => {
    if (
      initialData?.qualification_minutes_file &&
      typeof initialData.qualification_minutes_file === "object"
    ) {
      setQualificationFile(initialData.qualification_minutes_file);
    }
  }, [initialData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const maxSize = 1 * 1024 * 1024; // 1MB

      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a PDF or Word document");
        e.target.value = ""; // Clear the input
        return;
      }

      if (file.size > maxSize) {
        toast.error("File size should be less than 1MB");
        e.target.value = ""; // Clear the input
        return;
      }

      setQualificationFile(file);
    }
  };

  // Auto-calculate project cost when shares change
  const handleShareChange = (field, value) => {
    formik.setFieldValue(field, value);

    // Auto-calculate project costs
    if (field.includes("pkr")) {
      const applicantPKR =
        field === "applicant_share_pkr"
          ? parseFloat(value) || 0
          : parseFloat(formik.values.applicant_share_pkr) || 0;
      const unidoPKR =
        field === "unido_grant_share_pkr"
          ? parseFloat(value) || 0
          : parseFloat(formik.values.unido_grant_share_pkr) || 0;
      const sedfPKR =
        field === "sedf_share_pkr"
          ? parseFloat(value) || 0
          : parseFloat(formik.values.sedf_share_pkr) || 0;
      formik.setFieldValue("project_cost_pkr", applicantPKR + unidoPKR + sedfPKR);
    }

    if (field.includes("euro")) {
      const applicantEuro =
        field === "applicant_share_euro"
          ? parseFloat(value) || 0
          : parseFloat(formik.values.applicant_share_euro) || 0;
      const unidoEuro =
        field === "unido_grant_share_euro"
          ? parseFloat(value) || 0
          : parseFloat(formik.values.unido_grant_share_euro) || 0;
      const sedfEuro =
        field === "sedf_share_euro"
          ? parseFloat(value) || 0
          : parseFloat(formik.values.sedf_share_euro) || 0;
      formik.setFieldValue("project_cost_euro", applicantEuro + unidoEuro + sedfEuro);
    }

    setTimeout(() => formik.validateForm(), 0);
  };

  const handleNextClick = () => {
    if (loading) return;

    const projectCostPKR = parseFloat(formik.values.project_cost_pkr) || 0;
    const totalSharePKR =
      (parseFloat(formik.values.applicant_share_pkr) || 0) +
      (parseFloat(formik.values.unido_grant_share_pkr) || 0) +
      (parseFloat(formik.values.sedf_share_pkr) || 0);
    const projectCostEuro = parseFloat(formik.values.project_cost_euro) || 0;
    const totalShareEuro =
      (parseFloat(formik.values.applicant_share_euro) || 0) +
      (parseFloat(formik.values.unido_grant_share_euro) || 0) +
      (parseFloat(formik.values.sedf_share_euro) || 0);

    if (Math.abs(projectCostPKR - totalSharePKR) > 0.01) {
      toast.error(
        isSedf
          ? "Project Cost PKR must equal Applicant Share PKR + UNIDO Grant Share PKR + SEDF Share PKR"
          : "Project Cost PKR must equal Applicant Share PKR + UNIDO Grant Share PKR"
      );
      return;
    }

    if (Math.abs(projectCostEuro - totalShareEuro) > 0.01) {
      toast.error(
        isSedf
          ? "Project Cost Euro must equal Applicant Share Euro + UNIDO Grant Share Euro + SEDF Share Euro"
          : "Project Cost Euro must equal Applicant Share Euro + UNIDO Grant Share Euro"
      );
      return;
    }

    formik.validateForm().then((validationErrors) => {
      if (Object.keys(validationErrors).length > 0) {
        formik.setTouched(
          Object.keys(formik.values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        );
        return;
      }
      proceedWithNavigation();
    });
  };

  const proceedWithNavigation = () => {
    // Include the file when storing data
    const dataWithFile = {
      ...formik.values,
      qualification_minutes_file: qualificationFile,
    };

    if (onStoreFormData) {
      onStoreFormData(dataWithFile);
    }

    toast.success("Grant details saved");

    setTimeout(() => {
      if (onNext) {
        onNext();
      }
    }, 100);
  };

  const businessEntityOptions = [
    { label: "Partnership", value: "Partnership" },
    { label: "Private Ltd", value: "Private Ltd" },
    { label: "Sole Proprietor", value: "Sole Proprietor" },
    { label: "Association", value: "Association" },
    { label: "Other/Specify", value: "Other/Specify" },
  ];

  const toMillions = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "";
    return String(Number((num / 1000000).toFixed(6)));
  };

  return (
    <form onSubmit={submitFormWithErrors(formik)} className="space-y-6">
      {/* C1. Project Cost Details */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-700 mb-4">
          Project Cost - Amount of {cfg.label}{" "}
          <span className="text-red-500">*</span>
        </h3>

        <div
          className={`grid grid-cols-1 gap-x-4 gap-y-1 text-center ${
            isSedf ? "md:grid-cols-4" : "md:grid-cols-3"
          }`}
        >
          {/* Row: headers */}
          <h4 className="font-medium text-gray-600 text-center">
            Project Cost (Total) <span className="text-red-500">*</span>
          </h4>
          <h4 className="font-medium text-gray-600 text-center">
            UNIDO Grant Share <span className="text-red-500">*</span>
          </h4>
          {isSedf && (
            <h4 className="font-medium text-gray-600 text-center">
              SEDF Share <span className="text-red-500">*</span>
            </h4>
          )}
          <h4 className="font-medium text-gray-600 text-center">
            Applicant Share <span className="text-red-500">*</span>
          </h4>

          {/* Row: PKR (Amount) */}
          <ThinTextField
            label="Total PKR (Amount)"
            name="project_cost_pkr"
            type="number"
            step="0.01"
            value={formik.values.project_cost_pkr}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.project_cost_pkr && formik.errors.project_cost_pkr
            }
            placeholder="e.g. 1500000"
            disabled
          />
          <ThinTextField
            label="PKR (Amount)"
            name="unido_grant_share_pkr"
            type="number"
            step="0.01"
            value={formik.values.unido_grant_share_pkr}
            onChange={(e) =>
              handleShareChange("unido_grant_share_pkr", e.target.value)
            }
            onBlur={formik.handleBlur}
            error={
              formik.touched.unido_grant_share_pkr &&
              formik.errors.unido_grant_share_pkr
            }
            placeholder="e.g. 1500000"
          />
          {isSedf && (
            <ThinTextField
              label="PKR (Amount)"
              name="sedf_share_pkr"
              type="number"
              step="0.01"
              value={formik.values.sedf_share_pkr}
              onChange={(e) =>
                handleShareChange("sedf_share_pkr", e.target.value)
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.sedf_share_pkr && formik.errors.sedf_share_pkr
              }
              placeholder="e.g. 1500000"
            />
          )}
          <ThinTextField
            label="PKR (Amount)"
            name="applicant_share_pkr"
            type="number"
            step="0.01"
            value={formik.values.applicant_share_pkr}
            onChange={(e) =>
              handleShareChange("applicant_share_pkr", e.target.value)
            }
            onBlur={formik.handleBlur}
            error={
              formik.touched.applicant_share_pkr &&
              formik.errors.applicant_share_pkr
            }
            placeholder="e.g. 1500000"
          />

          {/* Row: PKR (Amount) error messages */}
          <div className="text-red-500 text-xs text-center leading-tight">
            {formik.touched.project_cost_pkr && formik.errors.project_cost_pkr}
          </div>
          <div className="text-red-500 text-xs text-center leading-tight">
            {formik.touched.unido_grant_share_pkr &&
              formik.errors.unido_grant_share_pkr}
          </div>
          {isSedf && (
            <div className="text-red-500 text-xs text-center leading-tight">
              {formik.touched.sedf_share_pkr && formik.errors.sedf_share_pkr}
            </div>
          )}
          <div className="text-red-500 text-xs text-center leading-tight">
            {formik.touched.applicant_share_pkr &&
              formik.errors.applicant_share_pkr}
          </div>

          {/* Row: PKR (Million) */}
          <ThinTextField
            label="Total PKR (Million)"
            value={toMillions(formik.values.project_cost_pkr)}
            disabled
            isRequired={false}
            placeholder="Auto-calculated"
          />
          <ThinTextField
            label="PKR (Million)"
            value={toMillions(formik.values.unido_grant_share_pkr)}
            disabled
            isRequired={false}
            placeholder="Auto-calculated"
          />
          {isSedf && (
            <ThinTextField
              label="PKR (Million)"
              value={toMillions(formik.values.sedf_share_pkr)}
              disabled
              isRequired={false}
              placeholder="Auto-calculated"
            />
          )}
          <ThinTextField
            label="PKR (Million)"
            value={toMillions(formik.values.applicant_share_pkr)}
            disabled
            isRequired={false}
            placeholder="Auto-calculated"
          />

          {/* Row: Euro (Amount) */}
          <ThinTextField
            label="Total Euro (Amount)"
            name="project_cost_euro"
            type="number"
            step="0.01"
            value={formik.values.project_cost_euro}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.project_cost_euro &&
              formik.errors.project_cost_euro
            }
            placeholder="e.g. 1500000"
            disabled
          />
          <ThinTextField
            label="Euro (Amount)"
            name="unido_grant_share_euro"
            type="number"
            step="0.01"
            value={formik.values.unido_grant_share_euro}
            onChange={(e) =>
              handleShareChange("unido_grant_share_euro", e.target.value)
            }
            onBlur={formik.handleBlur}
            error={
              formik.touched.unido_grant_share_euro &&
              formik.errors.unido_grant_share_euro
            }
            placeholder="e.g. 1500000"
          />
          {isSedf && (
            <ThinTextField
              label="Euro (Amount)"
              name="sedf_share_euro"
              type="number"
              step="0.01"
              value={formik.values.sedf_share_euro}
              onChange={(e) =>
                handleShareChange("sedf_share_euro", e.target.value)
              }
              onBlur={formik.handleBlur}
              error={
                formik.touched.sedf_share_euro && formik.errors.sedf_share_euro
              }
              placeholder="e.g. 1500000"
            />
          )}
          <ThinTextField
            label="Euro (Amount)"
            name="applicant_share_euro"
            type="number"
            step="0.01"
            value={formik.values.applicant_share_euro}
            onChange={(e) =>
              handleShareChange("applicant_share_euro", e.target.value)
            }
            onBlur={formik.handleBlur}
            error={
              formik.touched.applicant_share_euro &&
              formik.errors.applicant_share_euro
            }
            placeholder="e.g. 1500000"
          />

          {/* Row: Euro (Amount) error messages */}
          <div className="text-red-500 text-xs text-center leading-tight">
            {formik.touched.project_cost_euro &&
              formik.errors.project_cost_euro}
          </div>
          <div className="text-red-500 text-xs text-center leading-tight">
            {formik.touched.unido_grant_share_euro &&
              formik.errors.unido_grant_share_euro}
          </div>
          {isSedf && (
            <div className="text-red-500 text-xs text-center leading-tight">
              {formik.touched.sedf_share_euro && formik.errors.sedf_share_euro}
            </div>
          )}
          <div className="text-red-500 text-xs text-center leading-tight">
            {formik.touched.applicant_share_euro &&
              formik.errors.applicant_share_euro}
          </div>

          {/* Row: Euro (Million) */}
          <ThinTextField
            label="Total Euro (Million)"
            value={toMillions(formik.values.project_cost_euro)}
            disabled
            isRequired={false}
            placeholder="Auto-calculated"
          />
          <ThinTextField
            label="Euro (Million)"
            value={toMillions(formik.values.unido_grant_share_euro)}
            disabled
            isRequired={false}
            placeholder="Auto-calculated"
          />
          {isSedf && (
            <ThinTextField
              label="Euro (Million)"
              value={toMillions(formik.values.sedf_share_euro)}
              disabled
              isRequired={false}
              placeholder="Auto-calculated"
            />
          )}
          <ThinTextField
            label="Euro (Million)"
            value={toMillions(formik.values.applicant_share_euro)}
            disabled
            isRequired={false}
            placeholder="Auto-calculated"
          />
        </div>

        <div className="mt-4 p-3 bg-blue-50 rounded">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Project Cost = Applicant Share + UNIDO Grant
            Share{isSedf ? " + SEDF Share" : ""} (for both PKR and Euro)
          </p>
        </div>
      </div>

      {/* C2. Business Entity Type */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-700 mb-4">
          Business Entity Type <span className="text-red-500">*</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <ThinDropdown
              label="Business Entity Type"
              id="business-entity-dropdown"
              options={businessEntityOptions}
              value={formik.values.business_entity}
              onChange={formik.handleChange}
              name="business_entity"
              error={
                formik.touched.business_entity && formik.errors.business_entity
              }
            />
            {formik.touched.business_entity && formik.errors.business_entity && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.business_entity}
              </div>
            )}
          </div>

          {formik.values.business_entity === "Other/Specify" && (
            <div>
              <ThinTextField
                label="Specify Other Business Entity Type"
                name="business_entity_other"
                value={formik.values.business_entity_other}
                onChange={(e) =>
                  formik.setFieldValue("business_entity_other", e.target.value.replace(/[0-9]/g, ""))
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched.business_entity_other &&
                  formik.errors.business_entity_other
                }
                placeholder="Please specify"
              />
              {formik.touched.business_entity_other &&
                formik.errors.business_entity_other && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.business_entity_other}
                  </div>
                )}
            </div>
          )}
        </div>
      </div>

      {/* C3. Evaluation */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-700 mb-4">
          Evaluation <span className="text-red-500">*</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <ThinTextField
              label="Date of Evaluation/Qualification Review"
              name="evaluation_date"
              type="date"
              value={formik.values.evaluation_date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.evaluation_date && formik.errors.evaluation_date
              }
            />
            {formik.touched.evaluation_date &&
              formik.errors.evaluation_date && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.evaluation_date}
                </div>
              )}
          </div>

          <div>
            <ThinTextField
              label="Combined Score (%)"
              name="combined_score_percentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formik.values.combined_score_percentage}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.combined_score_percentage &&
                formik.errors.combined_score_percentage
              }
              placeholder="0.00"
            />
            {formik.touched.combined_score_percentage &&
              formik.errors.combined_score_percentage && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.combined_score_percentage}
                </div>
              )}
          </div>

          <div>
            <ThinTextField
              label="Grant Agreement #"
              name="grant_agreement_number"
              value={formik.values.grant_agreement_number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.grant_agreement_number &&
                formik.errors.grant_agreement_number
              }
              placeholder="Enter agreement number"
            />
            {formik.touched.grant_agreement_number &&
              formik.errors.grant_agreement_number && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.grant_agreement_number}
                </div>
              )}
          </div>

          <div>
            <ThinTextField
              label="Effective Date (Signed)"
              name="effective_date"
              type="date"
              value={formik.values.effective_date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.effective_date && formik.errors.effective_date
              }
            />
            {formik.touched.effective_date && formik.errors.effective_date && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.effective_date}
              </div>
            )}
          </div>

          <div>
            <ThinTextField
              label="Expiry Date"
              name="expiry_date"
              type="date"
              value={formik.values.expiry_date}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.expiry_date && formik.errors.expiry_date}
            />
            {formik.touched.expiry_date && formik.errors.expiry_date && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.expiry_date}
              </div>
            )}
          </div>

          <div>
            <ThinTextField
              label="Approved Grant Amount (PKR) (Million)"
              name="approved_grant_amount_pkr"
              type="number"
              step="0.000001"
              value={formik.values.approved_grant_amount_pkr}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.approved_grant_amount_pkr &&
                formik.errors.approved_grant_amount_pkr
              }
              placeholder="e.g. 1.5 (million)"
            />
            {formik.touched.approved_grant_amount_pkr &&
              formik.errors.approved_grant_amount_pkr && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.approved_grant_amount_pkr}
                </div>
              )}
          </div>

          <div>
            <ThinTextField
              label="Approved Grant Amount (EURO) (Million)"
              name="approved_grant_amount_euro"
              type="number"
              step="0.000001"
              value={formik.values.approved_grant_amount_euro}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.approved_grant_amount_euro &&
                formik.errors.approved_grant_amount_euro
              }
              placeholder="e.g. 1.5 (million)"
            />
            {formik.touched.approved_grant_amount_euro &&
              formik.errors.approved_grant_amount_euro && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.approved_grant_amount_euro}
                </div>
              )}
          </div>

          <div>
            <label className="text-[0.9rem] text-[#565656] mb-1 font-[InterLight] block">
              Upload Minutes of Qualification
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full"
            />
            {qualificationFile && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800 flex items-center">
                  <span className="mr-2">✓</span>
                  Selected:{" "}
                  <strong className="ml-1">{qualificationFile.name}</strong>
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Size: {(qualificationFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            )}
            {grantData?.qualification_minutes_file && !qualificationFile && (
              <p className="text-sm text-blue-600 mt-1">
                Current file:{" "}
                <a
                  href={`${process.env.REACT_APP_BASE_URL}${grantData.qualification_minutes_file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-800"
                >
                  View / Download
                </a>
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Accepted formats: PDF, DOC, DOCX (Max size: 1MB)
            </p>
          </div>

          <div>
            <ThinDropdown
              label="Minutes of Qualification Uploaded"
              id="qualification-uploaded-dropdown"
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
              value={
                formik.values.qualification_minutes_uploaded === ""
                  ? ""
                  : String(formik.values.qualification_minutes_uploaded)
              }
              onChange={(e) => {
                const boolValue = e.target.value === "true";
                formik.setFieldValue(
                  "qualification_minutes_uploaded",
                  boolValue
                );
                formik.setFieldTouched("qualification_minutes_uploaded", true, false);
              }}
              onBlur={() =>
                formik.setFieldTouched("qualification_minutes_uploaded", true)
              }
              name="qualification_minutes_uploaded"
              error={
                formik.touched.qualification_minutes_uploaded &&
                formik.errors.qualification_minutes_uploaded
              }
            />
            {formik.touched.qualification_minutes_uploaded &&
              formik.errors.qualification_minutes_uploaded && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.qualification_minutes_uploaded}
                </div>
              )}
          </div>
        </div>
      </div>

      <div className="pt-4 flex justify-end gap-3">
        {showNextOnly && !isEditMode && (
          <button
            type="button"
            onClick={handleNextClick}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Saving..." : "Next"}
          </button>
        )}
        {isEditMode && (
          <AddButton
            text="Update"
            type="submit"
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </form>
  );
};

export default GrantDetails;
